// 最底部狀態細條：版本號 + 本次遊玩時間（PlayTime，左）+ 系統時間（中）+ 交易 ID（TransText，右下角）。
// PlayTime 對應 Unity BottomBarView._playTimeText（本 session 累計，本地計時）；系統時間對照 OtherInfo/SystemTime。
// TransText（右下角交易 ID）＝本移植對齊 Unity 的重點：
//   - 資料源：後端 envelope ResponseInfo.TxnId（欄位 5），由 GameController 經 transactionIdChanged 事件送來。
//   - 文字格式：`Transaction {txnId}`，空字串則不顯示（對照 Unity BottomBarView.OnUpdateTransText:618-623，
//     其為硬編英文、含 // TODO: 多語系，故本移植同樣不在地化）。
//   - 時機：付費 spin 派發當下清空、收到回應填入、免費遊戲沿用觸發那筆（GameController 控制）。
//   - 點擊複製原始單號（排除 "Transaction " 前綴），對照 Unity CopyTextToClipboard（_excludePrefix='Transaction '）。
//   - 位置：Unity OtherInfo/TransText 節點 anchor(1,0) pivot(1,…)＝畫面右下角（BottomBarView.prefab:1644/1664-1668）。
// [已知簡化] 本列的深色細條與三段式排版是本移植自訂，尚未完整照 Unity OtherInfo 群組（Net 延遲、40×40 圖示、
//   上下行箭頭等素材）還原；已對齊的只有 TransText 的資料流／文字格式／複製行為／右下角定位。
//   Unity OtherInfo 群組原值（供日後完整還原；群組 anchor(0.5,0) aPos (0,55) 底部錨定）：
//     橫版 VerText (−770,0)／Net (−550,0)／SystemTime (−30,0)／PlayTime (−710,0)／TransText anchor(1,0) aPos (870,0)
//     直版 VerText (−371,0)／TransText (480,0)／Net (−370,90)／SystemTime (266,90)／PlayTime (430,90)

import { Container, Graphics, Text } from 'pixi.js'
import type { EventBus } from '../../../framework/events/EventBus'
import type { GameEventMap } from '../../events/GameEvents'
import type { Orientation, Resizable } from '../../../framework/render/design'

const VERSION_LABEL = 'v.0.1.23.pixi'
/** Unity BottomBarView.OnUpdateTransText 的硬編前綴（$"Transaction {txnId}"）。 */
const TRANS_PREFIX = 'Transaction '

export class StatusBarView extends Container implements Resizable {
  private readonly bar = new Graphics()
  private readonly leftText = new Text({
    text: '',
    style: { fontFamily: 'monospace', fontSize: 12, fill: 0x9fb0c0 },
  })
  private readonly clockText = new Text({
    text: '',
    style: { fontFamily: 'monospace', fontSize: 12, fill: 0x9fb0c0 },
  })
  /** 右下角交易 ID（Unity OtherInfo/TransText），右對齊、可點擊複製。 */
  private readonly transText = new Text({
    text: '',
    style: { fontFamily: 'monospace', fontSize: 12, fill: 0x9fb0c0 },
  })
  private readonly clockTimer: number
  /** 本 session 起始時間（ms），用來計 PlayTime。瀏覽器 runtime，Date 可用。 */
  private readonly startMs = Date.now()
  /** 目前交易 ID 原始值（不含前綴），供點擊複製（對照 Unity CopyTextToClipboard 排除 'Transaction '）。 */
  private currentTxnId = ''

  constructor(bus: EventBus<GameEventMap>) {
    super()
    this.leftText.anchor.set(0, 0.5)
    this.clockText.anchor.set(0.5, 0.5)
    this.transText.anchor.set(1, 0.5)
    this.transText.eventMode = 'none' // 無交易 ID 時不可互動；setTxnId 有值時才開放點擊。
    this.transText.cursor = 'pointer'
    this.transText.on('pointertap', () => this.copyTxnId())
    this.addChild(this.bar, this.leftText, this.clockText, this.transText)

    // 交易 ID 變更（GameController：付費 spin 派發清空、回應填入；免費遊戲沿用；空字串＝不顯示）。
    bus.on('transactionIdChanged', ({ txnId }) => this.setTxnId(txnId))

    this.updateClock()
    // 瀏覽器 runtime 計時器（非 workflow 腳本環境），每秒更新時鐘與遊玩時間。
    this.clockTimer = window.setInterval(() => this.updateClock(), 1000)
  }

  /** 對照 Unity：txnId == "" ? "" : $"Transaction {txnId}"（空字串清空、否則加前綴）。 */
  private setTxnId(txnId: string): void {
    this.currentTxnId = txnId
    this.transText.text = txnId === '' ? '' : `${TRANS_PREFIX}${txnId}`
    this.transText.eventMode = txnId === '' ? 'none' : 'static'
  }

  /** 點擊交易 ID 複製原始單號（不含前綴），對照 Unity CopyTextToClipboard.Copy。 */
  private copyTxnId(): void {
    if (this.currentTxnId === '') return
    // 非安全內容（http）或不支援時 navigator.clipboard 為 undefined，靜默略過不打斷遊戲。
    const clipboard = navigator.clipboard as Clipboard | undefined
    if (clipboard === undefined) return
    void clipboard.writeText(this.currentTxnId).catch(() => {})
  }

  private updateClock(): void {
    const now = new Date()
    this.clockText.text = now.toLocaleTimeString('en-GB', { hour12: false })
    const elapsedSec = Math.max(0, Math.floor((Date.now() - this.startMs) / 1000))
    const mm = String(Math.floor(elapsedSec / 60)).padStart(2, '0')
    const ss = String(elapsedSec % 60).padStart(2, '0')
    this.leftText.text = `${VERSION_LABEL}    ⏳ ${mm}:${ss}`
  }

  resize(width: number, height: number, _orientation: Orientation): void {
    const barHeight = Math.max(18, height * 0.028)
    const y = height - barHeight
    this.bar.clear().rect(0, y, width, barHeight).fill({ color: 0x05070c, alpha: 0.7 })

    const fontSize = Math.max(9, barHeight * 0.55)
    this.leftText.style.fontSize = fontSize
    this.clockText.style.fontSize = fontSize
    this.transText.style.fontSize = fontSize
    const cy = y + barHeight / 2
    this.leftText.position.set(14, cy) // 左：版本 + 遊玩時間
    this.clockText.position.set(width / 2, cy) // 中：系統時鐘（對照 Unity SystemTime 置中偏右）
    this.transText.position.set(width - 14, cy) // 右下角：交易 ID（對照 Unity OtherInfo/TransText anchor(1,0)）
  }

  override destroy(): void {
    window.clearInterval(this.clockTimer)
    super.destroy()
  }
}
